// Blend ingredients. A recipe is [{ name, mg }] with mg per WHOLE vial. The stack item holds a
// default recipe; each vial holds its own COPY (never a reference), so editing the default never
// rewrites past shots. Shots store mL drawn, so each ingredient's mg is derived at display time
// as its share of the shot's total mg (see blendBreakdown). Pure — no React, no app imports — so tests hit it directly.

const round2 = n => Math.round(n * 100) / 100;

export const supportsBlend = dt => ['syringe', 'compounded'].includes(dt || 'syringe');

export const isBlend = b => Array.isArray(b) && b.length > 0;

const cleanName = name => String(name ?? '').trim().replace(/\s+/g, ' ').slice(0, 100);

export const ingredientKey = name => cleanName(name).toLowerCase();

export function normalizeBlend(rows) {
  if (!Array.isArray(rows)) return undefined;
  const out = rows
    .map(r => ({ name: cleanName(r?.name), mg: parseFloat(r?.mg) }))
    .filter(r => r.name && r.mg > 0)
    .map(r => ({ name: r.name, mg: round2(r.mg) }));
  return out.length ? out : undefined;
}

export const toBlendRows = b => (isBlend(b) ? b.map(i => ({ name: i.name, mg: String(i.mg) })) : []);

export const blendTotalMg = b => (isBlend(b) ? round2(b.reduce((s, i) => s + (parseFloat(i.mg) || 0), 0)) : 0);

export function blendConc(blend, totalVol) {
  const total = blendTotalMg(blend), vol = parseFloat(totalVol);
  return total > 0 && vol > 0 ? round2(total / vol) : 0;
}

// Conc to save for a blend vial. Re-derive only when the recipe or BAC actually changed: an overfill
// correction (useShotActions applySupplyDelta) moves totalVol without changing what was mixed, and
// re-deriving from that drifted volume on an unrelated edit would rewrite every past shot's mg.
export function blendVialConc(blend, totalVol, existing) {
  const unchanged = existing && isBlend(existing.blend) && sameBlend(blend, existing.blend)
    && parseFloat(totalVol) === parseFloat(existing.totalVol) && existing.conc > 0;
  return unchanged ? existing.conc : blendConc(blend, totalVol);
}

// Order-insensitive: reordering the same rows is not a different recipe.
export function sameBlend(a, b) {
  const sig = rows => (normalizeBlend(rows) || []).map(r => `${ingredientKey(r.name)}=${r.mg}`).sort().join('|');
  return sig(a) === sig(b);
}

export function scaleBlend(blend, targetTotalMg) {
  const total = blendTotalMg(blend), target = parseFloat(targetTotalMg);
  if (!(total > 0) || !(target > 0)) return blend;
  return blend.map(i => ({ name: i.name, mg: round2(i.mg * target / total) }));
}

// Each ingredient's share of the total mg the app already shows for this shot (mL × vial.conc),
// NOT shotMl × mg ÷ totalVol: an overfill correction moves totalVol but not conc, and the
// breakdown must always add up to the total displayed beside it.
export function blendBreakdown(vial, shotMl) {
  const conc = parseFloat(vial?.conc), ml = parseFloat(shotMl), total = blendTotalMg(vial?.blend);
  if (!(total > 0) || !(conc > 0) || !(ml > 0)) return [];
  return vial.blend.map(i => ({ name: i.name, mg: ml * conc * i.mg / total }));
}

// Same resolution order HistoryView uses for a shot's total mg, so the breakdown can never
// disagree with the total shown beside it.
export function vialForShot(vials, vialId) {
  const vs = vials || [];
  return vs.find(v => v.id === vialId) || vs.find(v => v.status === 'open' || v.active) || vs.find(v => v.conc) || null;
}

// mg/mL for a split-vial shot, whose mL came from two vials of possibly different strength.
// Newer shots carry it (shot.mgPerMl). Older single-injection syringe splits stored the physical
// total, so derive it from both vials and the new vial's splitOffset (mL taken from the old one).
// Older multi-injection and compounded splits stored mL in the old vial's terms, so the old vial's
// conc (the normal lookup) is already right for them. null = use the normal vial lookup.
export function splitShotConc(shot, vials, deliveryType) {
  const stamped = parseFloat(shot?.mgPerMl);
  if (stamped > 0) return stamped;
  if (!shot?.splitVialId || shot.totalDoses || deliveryType === 'compounded') return null;
  const vs = vials || [];
  const next = vs.find(v => v.id === shot.splitVialId);
  const prev = vs.find(v => v.id === (shot.splitPrevActiveVialId || shot.vial));
  const vol = parseFloat(shot.vol), a = parseFloat(prev?.conc), b = parseFloat(next?.conc);
  if (!(vol > 0) || !(a > 0) || !(b > 0)) return null;
  const fromPrev = Math.min(vol, Math.max(0, parseFloat(next.splitOffset) || 0));
  return (fromPrev * a + (vol - fromPrev) * b) / vol;
}

export const shotBreakdown = (shot, vials, deliveryType) => {
  if (!supportsBlend(deliveryType)) return [];
  const vial = vialForShot(vials, shot.vial);
  const conc = splitShotConc(shot, vials, deliveryType);
  return blendBreakdown(conc && vial ? { ...vial, conc } : vial, shot.vol);
};

// Later entries win the display spelling, so callers pass parts oldest → newest.
export function sumIngredients(parts) {
  const map = new Map();
  for (const p of parts) {
    const key = ingredientKey(p.name);
    const cur = map.get(key);
    map.set(key, { name: cleanName(p.name), mg: (cur?.mg || 0) + p.mg });
  }
  return [...map.values()].sort((a, b) => b.mg - a.mg || a.name.localeCompare(b.name));
}

export function ingredientTotals({ shots, vials, getCmpd }) {
  const sorted = [...(shots || [])].sort((a, b) => new Date(a.date) - new Date(b.date));
  return sumIngredients(sorted.flatMap(s => shotBreakdown(s, vials?.[s.compound], getCmpd(s.compound)?.deliveryType)));
}

// Below 0.01 mg, 2dp mg would round to "0mg" — show mcg instead.
const fmtAmount = (mg, digits) => (mg > 0 && mg < 0.01
  ? `${parseFloat((mg * 1000).toFixed(2))}mcg`
  : `${parseFloat(mg.toFixed(digits))}mg`);

export const formatBreakdown = (parts, sep = '; ', digits = 2) =>
  parts.map(p => `${p.name} ${fmtAmount(p.mg, digits)}`).join(sep);

// Spreadsheet text form of a recipe: "GHK-Cu 50; KPV 10" (mg per vial; a trailing "mg" is optional,
// names may contain commas). Used by Stack Import and the Stack/Vials CSV exports so they round-trip.
export const blendToText = blend => (isBlend(blend) ? blend.map(i => `${i.name} ${i.mg}`).join('; ') : '');

export function parseBlendText(text) {
  const entries = String(text ?? '').split(';').map(e => e.trim()).filter(Boolean);
  if (!entries.length) return { blend: undefined };
  const rows = [];
  for (const entry of entries) {
    const m = entry.match(/^(.*\S)\s+(\d+(?:\.\d+)?)\s*(?:mg)?$/i);
    if (!m || !(parseFloat(m[2]) > 0)) return { error: `Could not read blend ingredient "${entry}" — use "Name mg; Name mg", e.g. "GHK-Cu 50; KPV 10"` };
    rows.push({ name: m[1], mg: m[2] });
  }
  return { blend: normalizeBlend(rows) };
}

// Weekly per-ingredient totals for the Trends chart: ceil(rangeDays/7) consecutive 7-day buckets
// ending at `now`, each { start, totals: { [ingredientKey]: mg } }. Shots before the range are skipped;
// a shot dated after `now` (logged ahead) lands in the latest week, matching the totals table.
export function ingredientWeekly({ shots, vials, getCmpd, rangeDays, now = Date.now() }) {
  const WEEK = 7 * 864e5;
  const n = Math.max(1, Math.ceil(rangeDays / 7));
  const start = now - n * WEEK;
  const weeks = Array.from({ length: n }, (_, i) => ({ start: start + i * WEEK, totals: {} }));
  for (const s of shots || []) {
    const t = new Date(s.date).getTime();
    if (!(t >= start)) continue;
    const w = weeks[Math.min(n - 1, Math.floor((t - start) / WEEK))];
    for (const p of shotBreakdown(s, vials?.[s.compound], getCmpd(s.compound)?.deliveryType)) {
      const k = ingredientKey(p.name);
      w.totals[k] = (w.totals[k] || 0) + p.mg;
    }
  }
  return weeks;
}
